import CodeIcon from '@mui/icons-material/Code';
import DescriptionIcon from '@mui/icons-material/Description';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import FolderIcon from '@mui/icons-material/Folder';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import TerminalIcon from '@mui/icons-material/Terminal';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import {
  cyan,
  deepPurple,
  orange,
  indigo,
  blue,
  brown,
} from '@mui/material/colors';
import Skeleton from '@mui/material/Skeleton';
import styled from '@mui/material/styles/styled';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useUser } from '../../../contexts/UserContext';
import { formatDateTimeUTC } from '../../../utils/timeUtils';
import { formatBytes } from '../../../utils/utils';
import NoContent from '../../sections/no-content/NoContent';

const StyledTableCell = styled(TableCell)({
  fontSize: '0.8em',
  fontWeight: '600',
});

const fileIconDisplay = (dir, ext) => {
  // if it's a directory (dir), just show a folder
  if (dir) return <FolderIcon sx={{ fontSize: '1.2em', color: brown[300] }} />;

  switch (ext) {
    case 'log':
      return <FindInPageIcon sx={{ fontSize: '1.2em', color: blue[500] }} />;
    case 'sh':
      return <TerminalIcon sx={{ fontSize: '1.2em', color: orange[500] }} />;
    case 'txt':
      return <DescriptionIcon sx={{ fontSize: '1.2em', color: cyan[500] }} />;
    case 'xml':
      return <CodeIcon sx={{ fontSize: '1.2em', color: deepPurple[500] }} />;
    case 'zip':
      return <FolderZipIcon sx={{ fontSize: '1.2em', color: brown[500] }} />;
    default:
      return (
        <QuestionMarkIcon sx={{ fontSize: '1.1em', color: indigo[500] }} />
      );
  }
};

const FileDisplay = ({
  fileList,
  folderChain,
  loading,
  onChangeFolder,
  selectedFiles,
  selectFiles,
}) => {
  const { t } = useTranslation();
  const { user } = useUser();

  const handleClick = (event, file) => {
    // Handles multi-select through the use of the shift key.
    // Basically adds on to whatever is already selected.
    if (event.shiftKey) {
      const firstSelectedFile = selectedFiles[0];
      const firstSelectedIndex =
        fileList.findIndex((item) => item.id === firstSelectedFile?.id) || 0;

      const currentIndex = fileList.findIndex((item) => item.id === file.id);

      if (firstSelectedIndex < currentIndex) {
        selectFiles(fileList.slice(firstSelectedIndex, currentIndex + 1));
      } else if (firstSelectedIndex > currentIndex) {
        const lastSelectedFile = selectedFiles[selectedFiles.length - 1];
        const lastSelectedIndex = fileList.findIndex(
          (item) => item.id === lastSelectedFile.id
        );
        selectFiles(fileList.slice(currentIndex, lastSelectedIndex + 1));
      }
      // Handles multi-select through the use of the meta key.
      // Basically appends the new item to the list when selected,
      // or removes it when the selected item was already previously selected
    } else if (event.metaKey) {
      const fileAlreadySelected = selectedFiles.some(
        (item) => item.id === file.id
      );
      if (fileAlreadySelected) {
        const newFileList = selectedFiles.filter((val) => val.id !== file.id);
        selectFiles(newFileList);
      } else {
        const newFileList = [...selectedFiles, file];
        const sortedFileList = newFileList.sort((a, b) => a.id - b.id);
        selectFiles(sortedFileList);
      }
    } else {
      // if there is already a selected file, remove it and set the new selected file
      // if the file is selected already, unselect it.
      if (selectedFiles.find((f) => f.id === file.id)) {
        selectFiles([]);
      } else {
        selectFiles([file]);
        // if the user is holding command or alt, add the selected file to the list
      }
    }
  };

  const handleDoubleClick = (file) => {
    if (file.dir) {
      const newFolderChain = [...folderChain, file];
      onChangeFolder(newFolderChain);
    }
  };

  const isSelectedFile = (fileId) => {
    if (selectedFiles.find((f) => f.id === fileId)) {
      return blue[50];
    } else {
      return 'transparent';
    }
  };

  return (
    <Table size="small">
      <TableBody>
        {loading ? (
          <TableRow>
            <TableCell>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <CircularProgress
                  size="20px"
                  thickness={7}
                  sx={{ color: (theme) => theme.palette.grey[400] }}
                />
                <Skeleton variant="text" fontSize="0.8em" width={300} />
              </Box>
            </TableCell>
            <TableCell>
              <Skeleton variant="text" fontSize="0.8em" width={100} />
            </TableCell>
            <TableCell>
              <Skeleton variant="text" fontSize="0.8em" width={200} />
            </TableCell>
          </TableRow>
        ) : fileList.length > 0 ? (
          fileList.map((file) => (
            <TableRow
              key={file.id}
              sx={{
                borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                backgroundColor: isSelectedFile(file.id),
                cursor: 'pointer',
                userSelect: 'none',
              }}
              onClick={(e) => handleClick(e, file)}
              onDoubleClick={() => handleDoubleClick(file)}
            >
              <TableCell>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  {fileIconDisplay(file?.dir, file?.ext)}
                  <Typography fontSize="0.8em" fontWeight="600">
                    {file?.name}
                  </Typography>
                </Box>
              </TableCell>
              <StyledTableCell>
                {file?.size && formatBytes(file?.size)}
              </StyledTableCell>
              <StyledTableCell>
                {file?.date && formatDateTimeUTC(file?.date, user?.timeZone)}
              </StyledTableCell>
            </TableRow>
          ))
        ) : (
          <NoContent title={t('noFiles', { ns: 'fileBrowser' })} />
        )}
      </TableBody>
    </Table>
  );
};

FileDisplay.propTypes = {
  fileList: PropTypes.array,
  folderChain: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  onChangeFolder: PropTypes.func.isRequired,
  selectedFiles: PropTypes.array.isRequired,
  selectFiles: PropTypes.func.isRequired,
};

export default FileDisplay;
